import { useEffect, useRef, useState } from "react";
import { Trash2, Check, Loader2, ChevronDown } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { fetchSelectableUsers, updateNhiOwners, deleteNhi, NhiIdentifier } from "./Api/governanceApi";

export const getNhiIdentifier = (sa: any, cloudProvider: string, tenantName: string): NhiIdentifier => {
  const details = sa?.serviceDetails || {};
  const cp = cloudProvider.toLowerCase();
  if (cp === "gcp") {
    return { tenantName, cloudId: details.projectId || "", entityId: details.serviceAccountEmail || sa?.email || "" };
  }
  if (cp === "aws") {
    return { tenantName, cloudId: details.accountId || sa?.keyId || "", entityId: details.roleArn || sa?.email || "" };
  }
  return { tenantName, cloudId: details.subscriptionId || "", entityId: details.instanceId || sa?.keyId || "" };
};

interface NhiOwnerSelectorProps {
  cloudProvider: string;
  tenantName: string;
  identifier: NhiIdentifier;
  owners: string[];
  onUpdated: () => void;
}

export function NhiOwnerSelector({ cloudProvider, tenantName, identifier, owners, onUpdated }: NhiOwnerSelectorProps) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [localOwners, setLocalOwners] = useState<string[]>(owners || []);
  const [pendingOwners, setPendingOwners] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setLocalOwners(owners || []);
  }, [owners]);

  useEffect(() => {
    if (!open) return;
    const requestId = ++requestIdRef.current;
    setLoadingUsers(true);
    fetchSelectableUsers(tenantName, identifier.cloudId, cloudProvider)
      .then((fetched) => {
        if (requestId === requestIdRef.current) setUsers(fetched);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setUsers([]);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoadingUsers(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantName, identifier.cloudId, cloudProvider]);

  const toggleOwner = (user: string) => {
    const exists = localOwners.includes(user);
    const next = exists ? localOwners.filter((o) => o !== user) : [...localOwners, user];
    setPendingOwners(next);
  };

  const confirmChange = async () => {
    if (!pendingOwners) return;
    const next = pendingOwners;
    try {
      setSaving(true);
      await updateNhiOwners(cloudProvider, identifier, next);
      setLocalOwners(next);
      setPendingOwners(null);
      onUpdated();
    } catch {
      setPendingOwners(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-[11px] font-medium text-muted-foreground mr-1">Owner:</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/30 hover:bg-muted/60 px-2.5 py-1 transition-colors max-w-full"
          >
            {localOwners.length > 0 ? (
              <span className="flex items-center gap-1 flex-wrap">
                {localOwners.map((owner) => (
                  <Badge key={owner} variant="secondary" className="text-[11px] h-5 bg-primary/10 text-primary border-primary/20">
                    {owner}
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground font-medium">No owner assigned</span>
            )}
            <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 overflow-hidden" align="start" onWheel={(e) => e.stopPropagation()}>
          <Command>
            <CommandInput placeholder="Search users..." className="h-9 text-xs" />
            <CommandList className="max-h-64 overflow-y-auto overscroll-contain">
              {loadingUsers ? (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading users...
                </div>
              ) : (
                <>
                  <CommandEmpty className="py-6 text-center text-xs text-muted-foreground">No users found</CommandEmpty>
                  <CommandGroup>
                    {users.map((user) => {
                      const selected = localOwners.includes(user);
                      return (
                        <CommandItem
                          key={user}
                          value={user}
                          onSelect={() => toggleOwner(user)}
                          className="text-[13px] cursor-pointer"
                        >
                          <div
                            className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                              selected ? "bg-primary border-primary text-primary-foreground" : "border-border"
                            }`}
                          >
                            {selected && <Check className="h-3 w-3" />}
                          </div>
                          <span className="truncate" title={user}>{user}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AlertDialog open={pendingOwners !== null} onOpenChange={(o) => !o && setPendingOwners(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change owner</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to change the Owner of this service account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmChange(); }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface NhiFrequentUsersProps {
  frequentUsers: string[];
}

export function NhiFrequentUsers({ frequentUsers }: NhiFrequentUsersProps) {
  const list = frequentUsers || [];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-[11px] font-medium text-muted-foreground mr-1">
        Frequent Users:
      </div>
      {list.length > 0 ? (
        list.slice(0, 4).map((user) => (
          <Badge key={user} variant="outline" className="text-[11px] h-5">
            {user}
          </Badge>
        ))
      ) : (
        <span className="text-[11px] text-muted-foreground">None</span>
      )}
      {list.length > 4 && <span className="text-[11px] text-muted-foreground font-medium">+{list.length - 4}</span>}
    </div>
  );
}

interface NhiDeleteButtonProps {
  cloudProvider: string;
  identifier: NhiIdentifier;
  onDeleted: () => void;
}

export function NhiDeleteButton({ cloudProvider, identifier, onDeleted }: NhiDeleteButtonProps) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const deleted = await deleteNhi(cloudProvider, identifier);
      setOpen(false);
      if (deleted) onDeleted();
    } catch {
      setOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-900/50 dark:hover:bg-red-950/30"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <Trash2 className="w-3.5 h-3.5" />
        Delete
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete service account</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to permanently delete this service account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
